import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { TestSetService } from "../../services/TestSetService";
import { DatasetService } from "../../services/DatasetService";
import { usePermissions } from "../../PermissionsProvider";
import moment from "moment";
import { ChatCompletionRequest } from "../../model_gen/chat";
import { TestSet, TestRun, IntentGroup } from "../../model_gen/evaluation";
import { FinetuneDataset } from "../../model_gen/finetuning";
import { NEW_TEST_RUN_STORAGE_KEY } from "../TestRun/TestRunNew";
import { TestRunService } from "../../services/TestRunService";
import { IntentService } from "../../services/IntentService";
import { MultiLayerSemanticChart } from "../shared/MultiLayerSemanticChart";
import TestRunComparison from "../TestRun/TestRunComparison";
import TestSetAddedRequestsTable from "./TestSetAddedRequestsTable";
import TestRunsTable from "./TestRunsTable";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import OverflowTabs, { TabItem } from "../ui/overflow-tabs";
import { Badge } from "../ui/badge";
import { 
  AppWindowIcon,
  HistoryIcon,
  FileTextIcon,
  CalendarIcon,
  SearchIcon,
  EditIcon,
  StarIcon,
  GitCompareArrows
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { notification } from "antd";

interface TabDefinition {
  key: string;
  label: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

const TestSetDetail: React.FC = () => {
  const { intentGroupId, testSetId } = useParams();
  const { apiKey } = usePermissions();
  const [intentGroup, setIntentGroup] = useState<IntentGroup | null>(null);
  const [testSet, setTestSet] = useState<TestSet | null>(null);
  const [requests, setRequests] = useState<ChatCompletionRequest[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRequestsLoading, setIsRequestsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [requestPageSize, setRequestPageSize] = useState<number>(5);
  const [requestsByPage, setRequestsByPage] = useState<
    Record<number, ChatCompletionRequest[]>
  >({});
  const [testRuns, setTestRuns] = useState<TestRun[]>([]);
  const [isTestRunsLoading, setIsTestRunsLoading] = useState<boolean>(false);
  const [currentTestRunPage, setCurrentTestRunPage] = useState<number>(1);
  const [testRunsByPage, setTestRunsByPage] = useState<
    Record<number, TestRun[]>
  >({});
  const [totalTestRuns, setTotalTestRuns] = useState<number>(0);
  const [tagDistribution, setTagDistribution] = useState<any[]>([]);
  const [testRunProgress, setTestRunProgress] = useState<Record<string, any>>({});
  const navigate = useNavigate();
  const [isCompareMode, setIsCompareMode] = useState<boolean>(false);
  const [selectedTestRunIds, setSelectedTestRunIds] = useState<number[]>([]);
  const [selectedTestRunsMap, setSelectedTestRunsMap] = useState<
    Record<number, boolean>
  >({});
  const [isComparisonModalVisible, setIsComparisonModalVisible] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [sourceDataset, setSourceDataset] = useState<FinetuneDataset | null>(null);
  const [isSourceDatasetLoading, setIsSourceDatasetLoading] =
    useState<boolean>(false);

  useEffect(() => {
    if (intentGroupId) {
      IntentService.getIntentGroup(
        Number(intentGroupId),
        false,
        setIsLoading,
        apiKey
      ).subscribe(
        (intentGroup: IntentGroup) => {
          setIntentGroup(intentGroup);
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }, [intentGroupId]);

  useEffect(() => {
    if (testSetId) {
      setIsLoading(true);
      TestSetService.getTestSet(testSetId, setIsLoading, apiKey).subscribe(
        (data: TestSet) => {
          setTestSet(data);
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: `Failed to fetch test set: ${error}`,
          });
          setIsLoading(false);
        }
      );
      if (!requestsByPage[currentPage]) {
        fetchRequests(currentPage);
      } else {
        setRequests(requestsByPage[currentPage]);
      }
    }
  }, [testSetId, currentPage, requestPageSize]);

  useEffect(() => {
    const meta: any = (testSet as any)?.meta || {};
    const sourceType = meta.source_type;
    const datasetIdRaw = meta.dataset_id;
    const datasetId =
      typeof datasetIdRaw === "number"
        ? datasetIdRaw
        : datasetIdRaw
        ? Number(datasetIdRaw)
        : null;

    if (sourceType !== "dataset" || !datasetId || !Number.isFinite(datasetId)) {
      setSourceDataset(null);
      return;
    }

    const sub = DatasetService.getDataset(
      datasetId,
      setIsSourceDatasetLoading,
      apiKey
    ).subscribe(
      (dataset: FinetuneDataset) => {
        setSourceDataset(dataset);
      },
      (error: any) => {
        console.error("Error fetching source dataset for test set:", error);
        setSourceDataset(null);
      }
    );

    return () => sub.unsubscribe();
  }, [testSet?.id, apiKey]);

  useEffect(() => {
    if (testSetId && !testRunsByPage[currentTestRunPage]) {
      fetchTestRuns(currentTestRunPage);
    } else if (testRunsByPage[currentTestRunPage]) {
      setTestRuns(testRunsByPage[currentTestRunPage]);
    }
  }, [testSetId, currentTestRunPage]);

  useEffect(() => {
    if (!testSetId) return;
    const hasIncomplete = testRuns.some(
      (tr) => tr.status !== "COMPLETED" && tr.status !== "ERROR"
    );
    if (!hasIncomplete) return;

    const fetchProgress = () => {
      TestRunService.getTestRunProgressByTestSet(
        Number(testSetId),
        () => {},
        apiKey
      ).subscribe(
        (data: Record<string, any>) => {
          setTestRunProgress(data);
          const allDone = testRuns
            .filter((tr) => tr.status === "RUNNING" || tr.status === "PENDING")
            .every((tr) => {
              const p = data[tr.id!.toString()];
              return p && (p.status === "COMPLETED" || p.status === "ERROR");
            });
          if (allDone) {
            setTestRunsByPage({});
            fetchTestRuns(currentTestRunPage);
          }
        },
        () => {}
      );
    };

    fetchProgress();
    const interval = setInterval(fetchProgress, 5000);
    return () => clearInterval(interval);
  }, [testSetId, apiKey, testRuns.map((t) => t.status).join(",")]);

  useEffect(() => {
    if (testSetId) {
      TestSetService.getTestSetRequestDistribution(
        testSetId,
        null,
        apiKey
      ).subscribe(
        (response: any[]) => {
          setTagDistribution(response);
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: `Failed to fetch tag distribution: ${error}`,
          });
        }
      );
    }
  }, [testSetId]);

  const fetchRequests = (page: number) => {
    const offset = (page - 1) * requestPageSize;
    setIsRequestsLoading(true);
    TestSetService.getTestSetRequests(
      testSetId,
      -1,
      -1,
      offset,
      requestPageSize,
      null,
      apiKey
    ).subscribe(
      (response: { items: ChatCompletionRequest[]; total: number }) => {
        setRequests(response.items);
        setRequestsByPage((prev) => ({
          ...prev,
          [page]: response.items,
        }));
        setIsRequestsLoading(false);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to fetch requests: ${error}`,
        });
        setIsRequestsLoading(false);
      }
    );
  };

  const fetchTestRuns = (page: number) => {
    const offset = (page - 1) * 5;
    setIsTestRunsLoading(true);
    TestRunService.getTestRunsByTestSet(
      Number(testSetId),
      offset,
      5,
      null,
      apiKey
    ).subscribe(
      (response: { items: TestRun[]; total: number }) => {
        setTestRuns(response.items);
        setTotalTestRuns(response.total);
        setTestRunsByPage((prev) => ({
          ...prev,
          [page]: response.items,
        }));
        setIsTestRunsLoading(false);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to fetch test runs: ${error}`,
        });
        setIsTestRunsLoading(false);
      }
    );
  };

  const handleTagsChange = (requestId: string, newTags: string[]) => {
    setIsRequestsLoading(true);
    const request = requests.find((r) => r.request_id === requestId);
    if (!request) return;

    try {
      const metadata = JSON.parse(
        (request.metadata?._maitai_test_request_meta as string) || "{}"
      );
      metadata.tags = newTags;

      TestSetService.updateTestSetRequestTags(
        testSetId,
        requestId,
        newTags,
        null,
        apiKey
      ).subscribe(
        () => {
          setRequests((prev) =>
            prev.map((r) => {
              if (r.request_id === requestId) {
                return {
                  ...r,
                  metadata: {
                    ...r.metadata,
                    _maitai_test_request_meta: JSON.stringify(metadata),
                  },
                };
              }
              return r;
            })
          );
          setIsRequestsLoading(false);
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: "Failed to update tags",
          });
          setIsRequestsLoading(false);
        }
      );
    } catch (e) {
      console.error(e);
      setIsRequestsLoading(false);
    }
  };

  const updateTestSetRequest = (request: ChatCompletionRequest) => {
    setRequests((prev) =>
      prev.map((r) => (r.request_id === request.request_id ? request : r))
    );
  };

  const handleNewTestRun = () => {
    localStorage.removeItem(NEW_TEST_RUN_STORAGE_KEY);
    navigate(
      `/intent-group/${testSet?.intent_group_id}/testsets/${testSetId}/runs/new`
    );
  };

  const handleDeleteTestRun = (testRun: TestRun) => {
        TestRunService.deleteTestRun(testRun.id, null, apiKey).subscribe(
          () => {
        notification.success({
          message: "Test run deleted successfully",
          description: "The test run has been deleted successfully",
        });
            setTestRunsByPage({});
            setCurrentTestRunPage(1);
            fetchTestRuns(1);
          },
          (error: any) => {
            console.error(error);
            notification.error({
              message: "Error",
              description: `Failed to delete test run: ${error}`,
            });
          }
        );
  };

  const handleCompareClick = () => {
    setIsCompareMode(true);
    setSelectedTestRunIds([]);
    setSelectedTestRunsMap({});
  };

  const handleCancelCompare = () => {
    setIsCompareMode(false);
    setSelectedTestRunIds([]);
    setSelectedTestRunsMap({});
  };

  const handleCompareRuns = () => {
    setIsComparisonModalVisible(true);
  };

  const handleCloseComparisonModal = () => {
    setIsComparisonModalVisible(false);
    setIsCompareMode(false);
    setSelectedTestRunIds([]);
    setSelectedTestRunsMap({});
  };

  const handleTestRunSelect = (testRunId: number, checked: boolean) => {
    const updatedMap = { ...selectedTestRunsMap };
    updatedMap[testRunId] = checked;
    setSelectedTestRunsMap(updatedMap);

    const allSelectedIds = Object.entries(updatedMap)
      .filter(([_, isSelected]) => isSelected)
      .map(([id, _]) => Number(id));

    setSelectedTestRunIds(allSelectedIds);
  };



  // Tab configuration
  const tabDefinitions: TabDefinition[] = [
    {
      key: "overview",
      label: "Overview",
      icon: <AppWindowIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-8">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-border">
              <CardContent className="p-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <FileTextIcon className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground font-gc-reg">Total Requests</p>
              </div>
                  <p className="text-3xl font-gc-demi-bold text-foreground">
                    {(testSet?.meta?.request_count as number) || 0}
                  </p>
              </div>
              </CardContent>
            </Card>
            
            <Card className="border-border">
              <CardContent className="p-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground font-gc-reg">Date Created</p>
                </div>
                  <p className="text-lg font-gc-med text-foreground">
                    {testSet?.date_created
                      ? moment(testSet.date_created).format("MMM D, YYYY")
                      : "-"}
                  </p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-border">
              <CardContent className="p-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <StarIcon className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground font-gc-reg">Golden Status</p>
      </div>
                  <p className="text-lg font-gc-med text-foreground">
                    {(testSet as any)?.is_golden ? "Golden" : "Not Golden"}
                  </p>
          </div>
              </CardContent>
            </Card>
          </div>

      {/* Details Section */}
          <Card className="border-border">
            <CardContent className="p-6">
              <h3 className="text-lg font-gc-med text-foreground mb-4">Details</h3>
              <div className="space-y-6">
                {(() => {
                  const meta: any = (testSet as any)?.meta || {};
                  const sourceType = meta.source_type;
                  const datasetIdRaw = meta.dataset_id;
                  const datasetId =
                    typeof datasetIdRaw === "number"
                      ? datasetIdRaw
                      : datasetIdRaw
                      ? Number(datasetIdRaw)
                      : null;

                  if (
                    sourceType !== "dataset" ||
                    !datasetId ||
                    !Number.isFinite(datasetId)
                  )
                    return null;

                  const datasetName =
                    sourceDataset?.name ||
                    (isSourceDatasetLoading
                      ? `Dataset #${datasetId}`
                      : `Dataset #${datasetId}`);

                  return (
                    <div>
                      <p className="text-sm text-muted-foreground font-gc-reg mb-1">
                        Dataset
                      </p>
                      <Link
                        to={`/forge/datasets/${datasetId}`}
                        className="text-base font-gc-reg text-maitai-green hover:underline"
                      >
                        {datasetName}
                      </Link>
                    </div>
                  );
                })()}
                <div>
                  <p className="text-sm text-muted-foreground font-gc-reg mb-1">Description</p>
                  <p className="text-base font-gc-reg text-foreground whitespace-pre-wrap">
                {testSet?.description || "No description provided"}
                  </p>
                </div>
                
            {tagDistribution.length > 0 && (
                <div>
                    <p className="text-sm text-muted-foreground font-gc-reg mb-2">Request Distribution</p>
                    <div className="flex flex-wrap gap-2">
                    {tagDistribution.map(({ tag, count }) => (
                        <Badge key={tag} variant="secondary" className="font-gc-reg">
                        {tag} ({count})
                        </Badge>
                    ))}
                </div>
                  </div>
            )}
        </div>
            </CardContent>
          </Card>

      {/* Semantic Distribution Chart */}
      {testSet && (
        <MultiLayerSemanticChart
          chartData={[
            {
              entityType: "test_set",
              entityTypeId: testSetId || "",
              name: testSet?.name,
            },
          ]}
          apiKey={apiKey}
        />
      )}
    </div>
      )
    },
    {
      key: "test_runs",
      label: "Test Runs",
      icon: <HistoryIcon className="w-4 h-4" />,
      content: (
        <TestRunsTable
          testRuns={testRuns}
          loading={isTestRunsLoading}
          currentPage={currentTestRunPage}
          onPageChange={setCurrentTestRunPage}
          totalTestRuns={totalTestRuns}
          testRunProgress={testRunProgress}
          isCompareMode={isCompareMode}
          selectedTestRunsMap={selectedTestRunsMap}
          onTestRunSelect={handleTestRunSelect}
          onDeleteTestRun={handleDeleteTestRun}
          onNewTestRun={handleNewTestRun}
          onCompareClick={handleCompareClick}
          onCancelCompare={handleCancelCompare}
          onCompareRuns={handleCompareRuns}
          selectedTestRunIds={selectedTestRunIds}
          testSetId={testSetId || ""}
          intentGroupId={testSet?.intent_group_id || 0}
        />
      )
    },
    {
      key: "requests",
      label: "Requests",
      icon: <SearchIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-gc-med text-foreground">Requests</h3>
            <p className="text-sm text-muted-foreground">View and manage test set requests</p>
          </div>

          <TestSetAddedRequestsTable
            requests={requests}
            apiKey={apiKey}
            title={`Test Set Requests (${requests.length})`}
            showHeader={false}
          />
        </div>
      )
    }
  ];

  // Create tabs array for OverflowTabs component
  const tabs: TabItem[] = tabDefinitions.map(tab => ({
    key: tab.key,
    label: tab.label,
    icon: tab.icon
  }));

  if (isLoading) {
  return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
          {/* Header Card Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="h-4 bg-muted rounded w-32 animate-pulse mb-6" />
              <div className="space-y-2">
                <div className="h-10 bg-muted rounded w-64 animate-pulse" />
                <div className="h-5 bg-muted rounded w-96 animate-pulse" />
              </div>
            </CardContent>
          </Card>

          {/* Content Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="h-4 bg-muted rounded flex-1 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-8 animate-pulse" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/intent-groups")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Intent Groups
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button 
                    onClick={() => navigate(`/intent-group/${testSet?.intent_group_id}`)}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                  {intentGroup?.intent_name}
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button 
                    onClick={() => navigate(`/intent-group/${testSet?.intent_group_id}?tab=testsets`)}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                  Test Sets
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">{testSet?.name}</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                {testSet?.name}
                </h1>
                <p className="text-lg text-muted-foreground font-gc-reg flex items-center gap-2">
                  {intentGroup?.intent_name || "Unknown Intent Group"}
                  <Badge variant="secondary" className="font-gc-reg text-xs">Request-based</Badge>
                </p>
      </div>
          <Button
            onClick={() =>
              navigate(
                `/intent-group/${testSet?.intent_group_id}/testsets/${testSetId}/edit`
              )
                }
                className="gap-2"
              >
                <EditIcon className="w-4 h-4" />
            Edit
          </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card className="border-border">
          <CardContent className="p-0">
            {/* Tab Navigation */}
            <div className="border-b border-border">
              <OverflowTabs
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
          </div>

            {/* Tab Content */}
            <div className="p-8">
              {tabDefinitions.find(tab => tab.key === activeTab)?.content}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Comparison Modal */}
      <Dialog open={isComparisonModalVisible} onOpenChange={setIsComparisonModalVisible}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompareArrows className="w-5 h-5" />
              Test Run Comparison
            </DialogTitle>
          </DialogHeader>
        <TestRunComparison
          testRunIds={selectedTestRunIds}
          apiKey={apiKey}
          key={selectedTestRunIds.join("-")}
        />
          <DialogFooter>
            <Button onClick={handleCloseComparisonModal}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TestSetDetail;
